// The arrows in menus and panels (docs/tooltips-plan.md §6.6, D760–D763), and
// Enter on what has a menu of its own (§6.8, D804).
//
// Every menu answers the arrows the way a desktop menu does (D760): ↑ and ↓
// walk its rows around the ends, Home and End go to the first and the last,
// Enter or Space presses a row, → (or Enter) on a row with a submenu opens it
// and goes in, ← inside comes back and closes it. Escape stays the window's
// own (D230). A panel — the emoji, the stickers, the reactions — is walked in
// two dimensions (D763): its grid cell by cell, its strips of tabs along, ↑
// from the grid's top row up to the strip over it and on to the search field.
//
// **The focus never moves.** The item the keys are on is marked `data-key-on`
// and drawn as the pointer's hover is, and the card says the keys are driving
// (`data-keys`); the focus stays where it was. The composer keeps its caret
// under a menu opened over it, a panel's search field keeps what is being
// typed into it, and the desktop's floating layer (D227), which never takes
// the keyboard for a menu, needs none: the application window passes the keys
// on to it (D761), as it passes the Ctrl mode's (D744). A press is a click at
// the item's middle, so what it opens stands where a click would put it.
//
// The pointer takes over on a real move, and the keys start again from the
// item it was last on. A menu opened from the keyboard starts on its first row
// (D762). Enter on what has a context menu of its own — a message, a chat row
// — opens that menu where a right-click would, and Space is left to press the
// element itself (D804).
//
// The markup is what the menus and panels of both clients already draw:
//
//     .echo-popover, [role=menu]    a card, or a menu standing on its own
//     .echo-menu, [role=menu]       a column of rows; .echo-menu--submenu one
//                                   hung beside its row, in .echo-popover__flyout
//     [aria-haspopup]               a row that opens a submenu; open while
//                                   aria-expanded="true" or --active
//     .emoji-panel__grid            a grid; .quick-reactions, .emoji-panel__tabs,
//                                   .sticker-panel__strip and
//                                   .echo-segmented-control rows
//     data-menukeys-relay           on the desktop's sheet: the card is drawn in
//                                   the floating layer, which has no keyboard
//     data-context-menu             an element with a context menu of its own,
//                                   which Enter opens (D804)
//
//     window.__echoMenuKeys.remote(msg)   the relay's far end: «key:ArrowDown»,
//                                         «key:Enter», …, «first»
//     window.__echoMenuKeys.on()          the item the keys are on, or null
//     window.__echoMenuKeysRelay(msg)     the client's, where a menu can stand
//                                         in another window: hands a key over
//
// Shared by both clients for the reason `tips.js` is: the document is the one
// complete list of what is on screen.
(function () {
  "use strict";

  if (typeof window === "undefined" || window.__echoMenuKeys) return;

  var SURFACE = ".echo-popover, [role=menu]";
  var LANE =
    ".echo-menu, [role=menu], .quick-reactions, .emoji-panel__tabs, .sticker-panel__strip, " +
    ".emoji-panel__grid, .echo-segmented-control";
  var COLUMN = ".echo-menu, [role=menu]";
  var GRID = ".emoji-panel__grid";
  var SUBMENU = ".echo-menu--submenu";
  var FLYOUT = "echo-popover__flyout";
  var ITEM =
    ".echo-menu__item, [role=menuitem], [role=menuitemradio], [role=menuitemcheckbox], " +
    ".quick-reactions__cell, .emoji-panel__tab, .emoji-panel__glyph, " +
    ".sticker-panel__tab, .sticker-panel__cell, .echo-segmented-control__item";
  var FIELD =
    "input:not([type]), input[type=text], input[type=search], textarea, " +
    "[contenteditable=''], [contenteditable=true]";
  var RELAY = "[data-menukeys-relay]";
  var MENU_OF = "[data-context-menu]";
  var ON = "data-key-on";
  var KEYS = "data-keys";
  // The keys this file answers to, by `KeyboardEvent.key` («Space» for « »).
  // The desktop's relay lets only these through (`floating::relay_menu_keys`).
  var NAMES = {
    ArrowUp: 1,
    ArrowDown: 1,
    ArrowLeft: 1,
    ArrowRight: 1,
    Home: 1,
    End: 1,
    Enter: 1,
    Space: 1,
  };
  // D762: how long after a key a menu that comes up is the key's; and how long
  // a submenu or a card is waited for. Long enough for a menu whose arrival
  // waits on a view transition in a busy or background window (the web's
  // `swap`); a press of the pointer in between ends the wait.
  var OPENED_BY_KEY = 2000;
  var POLL = 16;

  var cur = null; // where the next key starts: the item keyed, or pointed at last
  var drawn = null; // the item carrying the mark
  var played = null; // a sticker the keys set playing (D571's `active`)
  var pointerAt = -1; // when the pointer last pressed
  // Where the pointer stood at the last move seen since the keys took over:
  // none yet after a key, so the first move — the one a card coming up under
  // a still pointer is sent — only says where it is.
  var lastX = NaN;
  var lastY = NaN;
  var polling = 0;

  // ── What there is ──

  function disabled(el) {
    return el.disabled === true || el.getAttribute("aria-disabled") === "true";
  }

  function shown(el) {
    return el.getClientRects().length > 0 && el.closest("[hidden]") === null;
  }

  // A grid a search stands over keeps its scroll by `visibility`
  // (`.emoji-panel__grid--away`), and is no lane while it does.
  function laneShown(lane) {
    return shown(lane) && window.getComputedStyle(lane).visibility !== "hidden";
  }

  function isField(el) {
    return el instanceof Element && el.matches(FIELD);
  }

  // The card an element stands on: its outermost surface.
  function rootOf(el) {
    var root = null;
    for (var at = el; at !== null; at = at.parentElement) {
      if (at.matches(SURFACE)) root = at;
    }
    return root;
  }

  // The card on top: the last one drawn.
  function surface() {
    var all = document.querySelectorAll(SURFACE);
    for (var i = all.length - 1; i >= 0; i--) {
      if (rootOf(all[i]) === all[i] && shown(all[i])) return all[i];
    }
    return null;
  }

  function laneOf(el) {
    var up = el.parentElement;
    return up === null ? null : up.closest(LANE);
  }

  function kindOf(lane) {
    if (lane.matches(COLUMN)) return "column";
    if (lane.matches(GRID)) return "grid";
    return "row";
  }

  function itemsOf(lane) {
    var out = [];
    var all = lane.querySelectorAll(ITEM);
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (laneOf(el) !== lane || disabled(el) || el.getClientRects().length === 0) continue;
      out.push(el);
    }
    return out;
  }

  // The card's lanes with something in them, in their order.
  function lanes(root) {
    var out = [];
    var all = [];
    if (root.matches(LANE)) all.push(root);
    var inner = root.querySelectorAll(LANE);
    for (var i = 0; i < inner.length; i++) all.push(inner[i]);
    for (var j = 0; j < all.length; j++) {
      if (laneShown(all[j]) && itemsOf(all[j]).length > 0) out.push(all[j]);
    }
    return out;
  }

  // What ↑ and ↓ go between in a card: its lanes, and the fields typed into
  // above them — a panel's search.
  function stops(root) {
    var out = lanes(root);
    var fields = root.querySelectorAll(FIELD);
    for (var i = 0; i < fields.length; i++) {
      if (shown(fields[i]) && !disabled(fields[i])) out.push(fields[i]);
    }
    return out;
  }

  function current(root) {
    if (cur === null || !cur.isConnected || !root.contains(cur)) return null;
    if (disabled(cur) || cur.getClientRects().length === 0) return null;
    var lane = laneOf(cur);
    return lane !== null && root.contains(lane) && laneShown(lane) ? cur : null;
  }

  // ── Geometry ──

  function box(el) {
    return el.getBoundingClientRect();
  }

  function midX(r) {
    return r.left + r.width / 2;
  }

  function midY(r) {
    return r.top + r.height / 2;
  }

  // The stop nearest above (d < 0) or below (d > 0) the box `r`, among those
  // over or under some of its width.
  function stopFrom(root, r, d, except) {
    var all = stops(root);
    var best = null;
    var gap = Infinity;
    for (var i = 0; i < all.length; i++) {
      if (all[i] === except) continue;
      var b = box(all[i]);
      if (b.right <= r.left || b.left >= r.right) continue;
      var g = d < 0 ? r.top - b.bottom : b.top - r.bottom;
      if (g < -1 || g >= gap) continue;
      best = all[i];
      gap = g;
    }
    return best;
  }

  // The item nearest across to `x`; the first with no `x`.
  function nearest(items, x) {
    if (x === null) return items[0];
    var best = items[0];
    var far = Infinity;
    for (var i = 0; i < items.length; i++) {
      var dx = Math.abs(midX(box(items[i])) - x);
      if (dx < far) {
        best = items[i];
        far = dx;
      }
    }
    return best;
  }

  // The items of the row of a grid nearest above (d < 0) or below (d > 0)
  // `from`, of those in `items`.
  function rowFrom(items, y, half, d) {
    var edge = d < 0 ? -Infinity : Infinity;
    var i;
    for (i = 0; i < items.length; i++) {
      var my = midY(box(items[i]));
      if (d < 0 ? my < y - half && my > edge : my > y + half && my < edge) edge = my;
    }
    if (!isFinite(edge)) return [];
    var out = [];
    for (i = 0; i < items.length; i++) {
      if (Math.abs(midY(box(items[i])) - edge) < half) out.push(items[i]);
    }
    return out;
  }

  // A tab already marked as the one on: where the keys enter a strip.
  function marked(items) {
    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      if (el.getAttribute("aria-selected") === "true" || el.getAttribute("aria-pressed") === "true") return el;
      if (/--(on|selected)(\s|$)/.test(el.className || "")) return el;
    }
    return null;
  }

  // ── The mark ──

  function hideTip() {
    if (window.__echoTips) window.__echoTips.hide();
  }

  function play(el) {
    if (played !== null) {
      played.removeAttribute("active");
      played = null;
    }
    var sticker = el === null ? null : el.querySelector("echo-sticker");
    if (sticker !== null && !sticker.hasAttribute("active")) {
      sticker.setAttribute("active", "true");
      played = sticker;
    }
  }

  // The keys are on `el` now: marked, in view, its tip up.
  function key(el) {
    if (!el) return true;
    if (drawn !== null && drawn !== el) {
      drawn.removeAttribute(ON);
      var was = rootOf(drawn);
      if (was !== null && was !== rootOf(el)) was.removeAttribute(KEYS);
    }
    drawn = el;
    cur = el;
    lastX = NaN;
    lastY = NaN;
    el.setAttribute(ON, "");
    var root = rootOf(el);
    if (root !== null) root.setAttribute(KEYS, "");
    play(el);
    into(el);
    if (window.__echoTips && window.__echoTips.show) window.__echoTips.show(el);
    return true;
  }

  // The keys let go: nothing marked, the pointer's hover drawn again.
  function unkey() {
    if (drawn !== null) {
      drawn.removeAttribute(ON);
      var root = rootOf(drawn);
      if (root !== null) root.removeAttribute(KEYS);
    }
    drawn = null;
    play(null);
  }

  // Into view, by hand and in each box's own pixels: `scrollIntoView` would
  // move every clipping box up to the window, and the composer's panel is
  // zoomed (D683), its scroll counted unzoomed.
  function into(el) {
    var root = rootOf(el);
    for (var up = el.parentElement; up !== null; up = up.parentElement) {
      var s = window.getComputedStyle(up);
      var ys = (s.overflowY === "auto" || s.overflowY === "scroll") && up.scrollHeight > up.clientHeight + 1;
      var xs = (s.overflowX === "auto" || s.overflowX === "scroll") && up.scrollWidth > up.clientWidth + 1;
      if (ys || xs) {
        var r = box(el);
        var b = box(up);
        if (ys) {
          var ky = up.offsetHeight > 0 ? b.height / up.offsetHeight : 1;
          if (r.top < b.top) up.scrollTop -= (b.top - r.top) / ky;
          else if (r.bottom > b.bottom) up.scrollTop += (r.bottom - b.bottom) / ky;
        }
        if (xs) {
          var kx = up.offsetWidth > 0 ? b.width / up.offsetWidth : 1;
          if (r.left < b.left) up.scrollLeft -= (b.left - r.left) / kx;
          else if (r.right > b.right) up.scrollLeft += (r.right - b.right) / kx;
        }
      }
      if (up === root) break;
    }
  }

  // ── Pressing ──

  // A click at the item's middle, as the pointer's would be: what it opens
  // opens there, and a submenu stands beside its row (D428) rather than
  // against the menu's top as a click with no coordinates had it (D307).
  function press(el) {
    hideTip();
    var r = box(el);
    var x = midX(r);
    var y = midY(r);
    el.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        detail: 1,
        clientX: x,
        clientY: y,
        screenX: (window.screenX || 0) + x,
        screenY: (window.screenY || 0) + y,
      }),
    );
  }

  // Asks again every frame until `test` answers, for `ms` at most.
  function poll(test, ms) {
    if (polling) window.clearTimeout(polling);
    polling = 0;
    var until = Date.now() + ms;
    (function again() {
      polling = 0;
      if (test()) return;
      if (Date.now() < until) polling = window.setTimeout(again, POLL);
    })();
  }

  function opener(el) {
    return el.hasAttribute("aria-haspopup");
  }

  function opened(el) {
    return el.getAttribute("aria-expanded") === "true" || el.classList.contains("echo-menu__item--active");
  }

  // The submenu up beside a column, if one is.
  function besideOf(column) {
    var row = column.parentElement;
    if (row === null) return null;
    for (var i = 0; i < row.children.length; i++) {
      var slot = row.children[i];
      if (!slot.classList.contains(FLYOUT)) continue;
      var sub = slot.querySelector(COLUMN);
      if (sub !== null && laneShown(sub)) return sub;
    }
    return null;
  }

  // → on a row with a submenu: opened if it is not, and the keys go in on
  // its first row once it has one — a list asked for may come a moment later.
  function openSub(el) {
    var column = laneOf(el);
    if (column === null) return;
    if (!opened(el) || besideOf(column) === null) press(el);
    poll(function () {
      if (!el.isConnected) return true;
      var sub = besideOf(column);
      if (sub === null || !opened(el)) return false;
      var items = itemsOf(sub);
      if (items.length === 0) return false;
      key(items[0]);
      return true;
    }, OPENED_BY_KEY);
  }

  // ← in a submenu: back to the row that opened it, whose press closes it.
  function back(sub) {
    var row = sub.closest("." + FLYOUT);
    row = row === null ? null : row.parentElement;
    if (row === null) return;
    var column = null;
    for (var i = 0; i < row.children.length && column === null; i++) {
      if (row.children[i].matches(COLUMN)) column = row.children[i];
    }
    if (column === null) return;
    var openers = itemsOf(column).filter(opener);
    var open = openers.filter(opened)[0] || openers[0];
    if (!open) return;
    if (opened(open)) press(open);
    key(open);
  }

  // ── Walking ──

  // Into a lane from above or below, at `x` across where that means anything;
  // into a field, the keys let go and the field has them.
  function enter(to, fromAbove, x) {
    if (isField(to)) {
      unkey();
      if (document.activeElement !== to) to.focus();
      return true;
    }
    var items = itemsOf(to);
    if (items.length === 0) return true;
    var kind = kindOf(to);
    if (kind === "column") return key(fromAbove ? items[0] : items[items.length - 1]);
    if (kind === "row") return key(marked(items) || nearest(items, x));
    // A grid: the row at its near edge of what is in view.
    var b = box(to);
    var seen = items.filter(function (el) {
      var r = box(el);
      return r.bottom > b.top + 1 && r.top < b.bottom - 1;
    });
    if (seen.length === 0) seen = items;
    var r0 = box(seen[0]);
    var edge = rowFrom(seen, fromAbove ? -Infinity : Infinity, r0.height / 2, fromAbove ? 1 : -1);
    return key(nearest(edge.length > 0 ? edge : seen, x));
  }

  // The first row of a menu (D762); a panel starts on nothing, its search
  // field having the keys. True once there is a card to say it of.
  function first(root) {
    var all = lanes(root);
    if (all.length === 0) return false;
    for (var i = 0; i < all.length; i++) {
      if (kindOf(all[i]) === "column" && !all[i].matches(SUBMENU)) {
        key(itemsOf(all[i])[0]);
        return true;
      }
    }
    return true;
  }

  // A key with nothing under the keys yet.
  function begin(root, all, name, field, inside) {
    var columns = all.filter(function (lane) {
      return kindOf(lane) === "column" && !lane.matches(SUBMENU);
    });
    if (columns.length > 0) {
      if (name === "ArrowDown" || name === "Home") return key(itemsOf(columns[0])[0]);
      if (name === "ArrowUp" || name === "End") {
        var last = itemsOf(columns[columns.length - 1]);
        return key(last[last.length - 1]);
      }
      // ←, →, Enter and Space are a floating menu's while it is up, with
      // nothing to do yet; a menu standing in a sheet leaves them be.
      return root.matches(".echo-popover");
    }
    // A panel: into its grid — the strips are reached from the grid's top.
    var grids = all.filter(function (lane) {
      return kindOf(lane) === "grid";
    });
    if (inside) {
      var fb = box(field);
      if (name === "ArrowDown") {
        for (var i = 0; i < grids.length; i++) {
          if (box(grids[i]).top >= fb.bottom - 1) return enter(grids[i], true, null);
        }
        var below = stopFrom(root, fb, 1, field);
        return below === null ? false : enter(below, true, null);
      }
      if (name === "ArrowUp") {
        var above = stopFrom(root, fb, -1, field);
        return above === null ? false : enter(above, false, null);
      }
      return false;
    }
    if (name === "Enter" || name === "Space") return false;
    // A field outside the panel — the composer under its stickers — keeps
    // its caret and its Home and End: the panel is entered from above.
    if (field !== null && name !== "ArrowDown" && name !== "ArrowUp") return false;
    return enter(grids.length > 0 ? grids[0] : all[0], true, null);
  }

  function inColumn(root, lane, items, i, name) {
    var here = items[i];
    var sub = lane.matches(SUBMENU);
    if (name === "ArrowDown" || name === "ArrowUp") {
      var d = name === "ArrowDown" ? 1 : -1;
      if (i + d >= 0 && i + d < items.length) return key(items[i + d]);
      // Off an end: the rail over a message's menu, or round to the other end.
      var next = sub ? null : stopFrom(root, box(lane), d, lane);
      if (next !== null) return enter(next, d > 0, null);
      return key(items[d > 0 ? 0 : items.length - 1]);
    }
    if (name === "Home") return key(items[0]);
    if (name === "End") return key(items[items.length - 1]);
    if (name === "ArrowLeft") {
      if (sub) back(lane);
      return true;
    }
    if (name === "ArrowRight") {
      if (opener(here)) openSub(here);
      return true;
    }
    if (opener(here)) openSub(here);
    else press(here);
    return true;
  }

  // One key, in the window that draws the card. Whether it was taken.
  function handle(name, target, repeat) {
    var root = surface();
    if (root === null) return false;
    var all = lanes(root);
    if (all.length === 0) return false;
    var field = isField(target) ? target : null;
    var inside = field !== null && root.contains(field);
    var pressing = name === "Enter" || name === "Space";
    var here = current(root);
    // The item the pointer was on last, unmarked, is where the arrows start
    // again (D760) — not one a key presses, nor, off a menu's rows, one that
    // takes the caret's keys from a field beside the card (D763): a space
    // typed into the composer under its stickers is a space, not the sticker
    // the pointer passed over.
    if (here !== null && here !== drawn) {
      var away = field !== null && !inside && name !== "ArrowDown" && name !== "ArrowUp";
      if (pressing || (away && kindOf(laneOf(here)) !== "column")) here = null;
    }
    // A held Enter presses once, and its repeats go where the first press went:
    // `begin` only answers for Enter and Space, it moves nothing.
    if (repeat && pressing) return here !== null || begin(root, all, name, field, inside);
    hideTip();
    if (here === null) return begin(root, all, name, field, inside);
    var lane = laneOf(here);
    var items = itemsOf(lane);
    var i = items.indexOf(here);
    var kind = kindOf(lane);
    if (kind === "column") return inColumn(root, lane, items, i, name);
    if (name === "ArrowLeft") return key(items[Math.max(0, i - 1)]);
    if (name === "ArrowRight") return key(items[Math.min(items.length - 1, i + 1)]);
    if (name === "Home") return key(items[0]);
    if (name === "End") return key(items[items.length - 1]);
    if (name === "ArrowDown" || name === "ArrowUp") {
      var d = name === "ArrowDown" ? 1 : -1;
      if (kind === "grid") {
        var r = box(here);
        var row = rowFrom(items, midY(r), r.height / 2, d);
        if (row.length > 0) return key(nearest(row, midX(r)));
      }
      var next = stopFrom(root, box(lane), d, lane);
      return next === null ? true : enter(next, d > 0, midX(box(here)));
    }
    // Enter or Space: a space is typed while the search field has the focus.
    if (name === "Space" && inside) return false;
    press(here);
    return true;
  }

  // ── Keys ──

  function nameOf(e) {
    if (e.key === " " || e.key === "Spacebar") return "Space";
    return NAMES[e.key] === 1 && e.key !== "Space" ? e.key : "";
  }

  function send(msg) {
    if (typeof window.__echoMenuKeysRelay === "function") window.__echoMenuKeysRelay(msg);
  }

  function stop(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }

  // A key that can open a menu (D762): Enter or Space on a control, the Menu
  // key, Shift+F10, a chord with Ctrl or Alt.
  function opens(e, name) {
    if (e.repeat) return false;
    if (e.key === "ContextMenu" || (e.key === "F10" && e.shiftKey)) return true;
    if (name === "Enter" || name === "Space") {
      return !(e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) && !isField(e.target);
    }
    return (e.ctrlKey || e.altKey) && !e.metaKey && /^Key[A-Z]$/.test(e.code || "");
  }

  // A menu that comes up now is the keyboard's: its first row is keyed — here,
  // or in the floating layer, told so.
  function watch() {
    if (document.querySelector(RELAY) !== null || surface() !== null) return;
    var at = Date.now();
    poll(function () {
      if (pointerAt >= at) return true;
      if (document.querySelector(RELAY) !== null) {
        send("first");
        return true;
      }
      var root = surface();
      return root !== null && first(root);
    }, OPENED_BY_KEY);
  }

  // Enter on what has a context menu of its own opens it where a right-click
  // would, as the Menu key and Shift+F10 do (D307, D804): a `contextmenu` at
  // the middle of what of it is in the window, which each client answers as it
  // answers the pointer's — and the menu starts on its first row (D762).
  function contextMenu(target) {
    if (!(target instanceof Element) || !target.matches(MENU_OF)) return false;
    var r = box(target);
    var left = Math.max(r.left, 0);
    var right = Math.min(r.right, window.innerWidth);
    var top = Math.max(r.top, 0);
    var bottom = Math.min(r.bottom, window.innerHeight);
    if (right <= left || bottom <= top) return false;
    var x = (left + right) / 2;
    var y = (top + bottom) / 2;
    hideTip();
    watch();
    target.dispatchEvent(
      new MouseEvent("contextmenu", {
        bubbles: true,
        cancelable: true,
        button: 2,
        clientX: x,
        clientY: y,
        screenX: (window.screenX || 0) + x,
        screenY: (window.screenY || 0) + y,
      }),
    );
    return true;
  }

  // The first of all listeners: a key a menu takes reaches nothing else — not
  // the composer, which would send on it, nor the feed's walk under it.
  window.addEventListener(
    "keydown",
    function (e) {
      var name = nameOf(e);
      if (name !== "" && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey && !e.isComposing) {
        // A menu up in the other window: the keys are its (D761).
        if (document.querySelector(RELAY) !== null) {
          hideTip();
          stop(e);
          if (!(e.repeat && (name === "Enter" || name === "Space"))) send("key:" + name);
          return;
        }
        if (handle(name, e.target, e.repeat)) {
          stop(e);
          return;
        }
        if (name === "Enter" && !e.repeat && contextMenu(e.target)) {
          stop(e);
          return;
        }
      }
      if (opens(e, name)) watch();
    },
    true,
  );

  // The pointer takes over when it really moves: not on the move the engine
  // makes up when a list scrolls or a card comes up under a pointer standing
  // still, which comes with the place it already had.
  document.addEventListener(
    "mousemove",
    function (e) {
      var wasX = lastX;
      var wasY = lastY;
      lastX = e.screenX;
      lastY = e.screenY;
      if (isNaN(wasX) || (wasX === lastX && wasY === lastY)) return;
      if (drawn !== null) unkey();
      var item = e.target instanceof Element ? e.target.closest(ITEM) : null;
      if (item !== null && rootOf(item) !== null) cur = item;
    },
    true,
  );

  document.addEventListener(
    "pointerdown",
    function () {
      pointerAt = Date.now();
    },
    true,
  );

  // The relay's far end (D761): keys pressed in the window under this card.
  function remote(msg) {
    msg = String(msg);
    if (msg === "first") {
      poll(function () {
        var root = surface();
        return root !== null && first(root);
      }, OPENED_BY_KEY);
      return;
    }
    if (msg.lastIndexOf("key:", 0) !== 0) return;
    var name = msg.slice(4);
    if (NAMES[name] === 1) handle(name, null, false);
  }

  window.__echoMenuKeys = {
    remote: remote,
    on: function () {
      return drawn !== null && drawn.isConnected ? drawn : null;
    },
  };
})();
